import { getIconName, IconName } from '@se-admin/falcon';
import { i18n, ITranslationsObject } from '@secret-escapes/i18n';

import { ISale, ISaleLabel } from '../../common/types/apiSchema';
import { ITranslationObject } from '../../common/types/translationUtils';
import { ISaleLabelProps, ISaleLabelTranslations } from '../SaleLabel';
import {
  SaleLabelVariant,
  TSaleLabelPropsArray,
  TSaleLabelRenderPropsArray,
} from '../SaleLabelList';
import { ISaleLabelListInput } from './interfaces';
/**
 * Given an array of ISaleLabel, it returns the configuration object which will be stored
 * in the state and later used to render the sale labels.
 */
export function getRenderLabels(saleLabels: ISaleLabel[]): TSaleLabelRenderPropsArray {
  if (saleLabels !== undefined) {
    return saleLabels.map(saleLabel => ({
      backgroundColor: saleLabel.hexCode,
      icon: getIconName(saleLabel.iconCode),
      show: true,
      tooltipTitleTranslationKey: saleLabel.labelKey,
      tooltipTranslationKey: saleLabel.summary,
      translationKey: saleLabel.labelKey,
    }));
  }

  return [];
}
/**
 * Given a object of sale label properties, it returns the configuration object which will be stored
 * in the state and later used to render the sale labels.
 */
export function createSaleLabelConfiguration(
  props: ISaleLabelListInput
): TSaleLabelRenderPropsArray {
  const {
    editorsPick,
    exclusive,
    promotion,
    deposit,
    zeroDeposit,
    canAddFlightsToHotel,
    smartStay,
    hasFlightsIncluded,
    currency,
    showOnlyText,
    showTooltips,
    saleLabels,
  } = props;

  const depositShowText: boolean =
    ((promotion === undefined || promotion === null) && !editorsPick && !exclusive) || showOnlyText;
  const canAddFlightsToHotelShowText: boolean =
    (depositShowText && !deposit && !zeroDeposit) || showOnlyText;
  const smartStayShowText: boolean =
    (canAddFlightsToHotelShowText && !canAddFlightsToHotel) || showOnlyText;
  const flightsIncludedShowText: boolean = (smartStayShowText && !smartStay) || showOnlyText;
  const saleLabelVariant: SaleLabelVariant = SaleLabelVariant.normal;

  let smartStayIcon: IconName = IconName.dollar;

  if (currency && currency === 'GBP') {
    smartStayIcon = IconName.pound;
  } else if (currency && currency === 'EUR') {
    smartStayIcon = IconName.euro;
  }

  const renderPropsFromSaleLabels: TSaleLabelRenderPropsArray = getRenderLabels(saleLabels);

  const renderPropsFromSale: TSaleLabelRenderPropsArray = [
    {
      backgroundColor: '#000000',
      icon: !showOnlyText && IconName.star,
      saleLabelVariant,
      show: editorsPick,
      translationKey: 'common.editorsPick.title',
    },
    {
      backgroundColor: '#ff8c00',
      saleLabelVariant,
      show: exclusive,
      tooltipTitleTranslationKey: showTooltips && 'exclusive.deals.label.text',
      tooltipTranslationKey: showTooltips && 'exclusive.deals.tooltip.description.text',
      translationKey: 'exclusive.deals.label.text',
    },
    {
      backgroundColor: promotion && promotion.hexCode,
      icon: !showOnlyText && promotion && getIconName(promotion.iconCode),
      saleLabelTranslations: {
        translatedTooltipTitle: promotion && promotion.label,
        translatedTooltipValue: promotion && promotion.description,
      },
      saleLabelVariant,
      show: promotion !== undefined && promotion !== null,
      text: !exclusive && !editorsPick && promotion && promotion.label,
    },
    {
      backgroundColor: '#000000',
      icon: !showOnlyText && IconName.deposit,
      saleLabelVariant,
      show: deposit,
      tooltipTitleTranslationKey: showTooltips && 'search.results.flashsale.depostTag',
      tooltipTranslationKey: showTooltips && 'offers.depositNowRestLater.text',
      translationKey: depositShowText && 'search.results.flashsale.depostTag',
    },
    {
      backgroundColor: '#000000',
      icon: !showOnlyText && IconName.deposit,
      saleLabelVariant,
      show: zeroDeposit,
      tooltipTitleTranslationKey: showTooltips && 'search.results.flashsale.zero.depositTag',
      tooltipTranslationKey: showTooltips && 'search.results.flashsale.zero.deposit.text',
      translationKey: depositShowText && 'search.results.flashsale.zero.depositTag',
    },
    {
      backgroundColor: '#5d8aa8',
      icon: !showOnlyText && IconName.plane,
      saleLabelVariant,
      show: canAddFlightsToHotel,
      tooltipTitleTranslationKey: showTooltips && 'hotel.plus.brand.name',
      tooltipTranslationKey: showTooltips && 'hotel.plus.brand.description',
      translationKey: canAddFlightsToHotelShowText && 'hotel.plus.brand.name',
    },
    {
      backgroundColor: '#03a9f4',
      icon: smartStayIcon,
      saleLabelVariant,
      show: smartStay,
      tooltipTitleTranslationKey: showTooltips && 'smart.stay.label',
      tooltipTranslationKey: showTooltips && 'smart.stay.tooltip',
      translationKey: smartStayShowText && 'smart.stay.label',
    },
    {
      backgroundColor: '#000000',
      icon: !showOnlyText && IconName.plane,
      saleLabelVariant,
      show: hasFlightsIncluded,
      tooltipTitleTranslationKey: showTooltips && 'search.results.flashsale.flightsTag',
      tooltipTranslationKey: showTooltips && 'sale.flights.included.tooltip',
      translationKey: flightsIncludedShowText && 'search.results.flashsale.flightsTag',
    },
  ].filter(o => o.show);

  return renderPropsFromSale.concat(renderPropsFromSaleLabels);
}
/**
 * Adds the translations to the sale label list properties and removes any object attributes which
 * aren't needed
 */
export function createSaleLabelProperties(
  saleLabelRenderProps: TSaleLabelRenderPropsArray,
  translationsObject: ITranslationObject
): TSaleLabelRenderPropsArray {
  return saleLabelRenderProps.map(p => {
    const {
      backgroundColor,
      icon,
      saleLabelTranslations,
      text,
      tooltipTranslationKey,
      tooltipTitleTranslationKey,
      translationKey,
    } = p;
    const iconObject: { icon: IconName } = { icon };
    const translatedLabelValue: string = translationKey
      ? i18n(translationKey, [], translationsObject)
      : text;

    const translatedTooltipValue: string = getTooltipValue(
      saleLabelTranslations,
      tooltipTranslationKey,
      translationsObject
    );

    const translatedTooltipTitle: string = getTooltipTitle(
      saleLabelTranslations,
      tooltipTitleTranslationKey,
      translationsObject
    );

    let saleLabelTranslationsObj: ISaleLabelTranslations = {};

    // This will build the sale label properties object. It will only assign a property to the object
    // if it is defined.
    if (translatedLabelValue) {
      saleLabelTranslationsObj = { ...saleLabelTranslationsObj, ...{ translatedLabelValue } };
    }
    if (translatedTooltipValue) {
      saleLabelTranslationsObj = { ...saleLabelTranslationsObj, ...{ translatedTooltipValue } };
    }
    if (translatedTooltipTitle) {
      saleLabelTranslationsObj = { ...saleLabelTranslationsObj, ...{ translatedTooltipTitle } };
    }
    let saleLabelProperties: ISaleLabelProps = { backgroundColor };

    // Istanbul is saying that the else is missing but we don't need it
    /* istanbul ignore else */
    if (iconObject) {
      saleLabelProperties = { ...saleLabelProperties, ...iconObject };
    }

    /* istanbul ignore else */
    if (translatedLabelValue || translatedTooltipValue || translatedTooltipTitle) {
      saleLabelProperties = {
        ...saleLabelProperties,
        ...{ saleLabelTranslations: saleLabelTranslationsObj },
      };
    }

    return saleLabelProperties;
  });
}
/**
 * Gets the tooltip title. It checks to see if there is a title on the promotion, if not then it gets
 * one from the i18n library.
 */
function getTooltipTitle(
  saleLabelTranslations: ISaleLabelTranslations,
  tooltipTranslationKey: string,
  translationsObject: ITranslationsObject
) {
  const promotionTooltipTitle: string =
    saleLabelTranslations && saleLabelTranslations.translatedTooltipTitle
      ? saleLabelTranslations.translatedTooltipTitle
      : undefined;

  return tooltipTranslationKey
    ? i18n(tooltipTranslationKey, [], translationsObject)
    : promotionTooltipTitle;
}

/**
 * Gets the tooltip value. It checks to see if there is a value on the promotion, if not then it gets
 * one from the i18n library.
 */
function getTooltipValue(
  saleLabelTranslations: ISaleLabelTranslations,
  tooltipTranslationKey: string,
  translationsObject: ITranslationObject
) {
  const promotionTooltipValue: string =
    saleLabelTranslations && saleLabelTranslations.translatedTooltipValue
      ? saleLabelTranslations.translatedTooltipValue
      : undefined;

  return tooltipTranslationKey
    ? i18n(tooltipTranslationKey, [], translationsObject)
    : promotionTooltipValue;
}

/**
 *
 * Gets the sale label properties from the sale card.
 */
function getSaleLabelPropertiesFromSaleCard(
  saleCard: ISale,
  isFlightFunctionalityEnabled: boolean,
  flightsAvailableAtCSPage: boolean,
  depositAvailableAtCSPage: boolean,
  isZeroDepositDisplayable: boolean,
  labelVariant: SaleLabelVariant
): ISaleLabelListInput {
  const isSmallVariant: boolean = SaleLabelVariant.small === labelVariant;

  return {
    canAddFlightsToHotel:
      !isSmallVariant &&
      saleCard.travel &&
      saleCard.travel.hasFlightsAvailable &&
      isFlightFunctionalityEnabled &&
      flightsAvailableAtCSPage,
    currency:
      saleCard.prices && saleCard.prices.currency ? saleCard.prices.currency.currencyCode : null,
    deposit: !isSmallVariant && saleCard.attributes.isDepositSale && depositAvailableAtCSPage,
    editorsPick: saleCard.attributes.isEditorsPick,
    exclusive: !isSmallVariant && saleCard.attributes.isExclusive,
    hasFlightsIncluded: saleCard.travel && saleCard.travel.hasFlightsIncluded,
    promotion: !isSmallVariant ? saleCard.promotion : null,
    saleLabels: saleCard.labels,
    showOnlyText: isSmallVariant,
    showTooltips: !isSmallVariant,
    smartStay: !isSmallVariant && saleCard.attributes.isSmartStay,
    zeroDeposit: !isSmallVariant && saleCard.attributes.isZeroDeposit && isZeroDepositDisplayable,
  };
}

type TSaleCardListPropertiesResolver = (
  saleCard: ISale,
  translationObject: ITranslationObject,
  isFlightFunctionalityEnabled: boolean,
  flightsAvailableAtCSPage: boolean,
  depositAvailableAtCSPage: boolean,
  isZeroDepositDisplayable: boolean,
  labelVariant: SaleLabelVariant
) => TSaleLabelRenderPropsArray;

export const getSaleLabelConfiguration: TSaleCardListPropertiesResolver = (
  saleCard,
  translationObject,
  isFlightFunctionalityEnabled,
  flightsAvailableAtCSPage,
  depositAvailableAtCSPage,
  isZeroDepositDisplayable,
  labelVariant
) => {
  const saleLabelInput: ISaleLabelListInput = getSaleLabelPropertiesFromSaleCard(
    saleCard,
    isFlightFunctionalityEnabled,
    flightsAvailableAtCSPage,
    depositAvailableAtCSPage,
    isZeroDepositDisplayable,
    labelVariant
  );
  const saleLabelRenderProps: TSaleLabelPropsArray = createSaleLabelConfiguration(saleLabelInput);

  return createSaleLabelProperties(saleLabelRenderProps, translationObject);
};
